import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CarePlanCreate, CarePlanResponse, MedicalInfoResponse } from "@/lib/types";
import api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CarePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  organizationId: string;
  carePlan: CarePlanResponse | null;
  onSuccess?: () => void;
}

const CarePlanModal: React.FC<CarePlanModalProps> = ({
  open,
  onOpenChange,
  patientId,
  organizationId,
  carePlan,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [carePlanType, setCarePlanType] = useState("chronic_condition");
  const [carePlanDetails, setCarePlanDetails] = useState("");
  const [instructions, setInstructions] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [medicalInfo, setMedicalInfo] = useState<MedicalInfoResponse[] | null>(null);
  const [isLoadingMedicalInfo, setIsLoadingMedicalInfo] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (carePlan) {
      setCarePlanType(carePlan.care_plan_type);
      setCarePlanDetails(JSON.stringify(carePlan.care_plan_details, null, 2));
    } else {
      resetForm();
    }
  }, [carePlan, open]);

  useEffect(() => {
    if (open && patientId && organizationId) {
      fetchMedicalInfo();
    }
  }, [open, patientId, organizationId]);

  const fetchMedicalInfo = async () => {
    if (!patientId || !organizationId) return;

    setIsLoadingMedicalInfo(true);
    try {
      const medInfo = await api.getMedicalInfo(patientId, organizationId);
      setMedicalInfo(medInfo);
    } catch (error) {
      console.error("Error fetching medical info:", error);
    } finally {
      setIsLoadingMedicalInfo(false);
    }
  };

  const resetForm = () => {
    setCarePlanType("chronic_condition");
    setCarePlanDetails("");
    setInstructions("");
  };

  const handleGenerateCarePlan = async () => {
    if (!medicalInfo || medicalInfo.length === 0) {
      toast({
        title: "Error",
        description: "No medical information available for this patient",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setCarePlanDetails("");

    try {
      // Extract chronic conditions and medications from medical info
      const chronicConditions = medicalInfo[0].chronic_conditions || [];
      const medications = medicalInfo[0].medications || [];

      // Format the conditions and medications for the AI
      const conditionsText = chronicConditions.length > 0 
        ? "Chronic Conditions: " + chronicConditions.map(c => c.chronic_condition).join(", ")
        : "No chronic conditions";
      
      const medsText = medications.length > 0
        ? "Medications: " + medications.map(m => typeof m === 'string' ? m : m.name_dosage).join(", ")
        : "No medications";

      // Create the messages for the AI
      const messages = [
        { role: "system", content: "You are a healthcare assistant helping to create a care plan for a patient." },
        { role: "user", content: `${conditionsText}\n${medsText}\n${instructions ? `Additional instructions: ${instructions}` : ""}\n\nCreate a care plan for this patient.` }
      ];

      // Stream the response
      await api.streamChatCompletion(messages, (chunk) => {
        setCarePlanDetails(prev => prev + chunk);
        
        // Auto-scroll to the bottom of the textarea
        if (textareaRef.current) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      });
    } catch (error) {
      console.error("Error generating care plan:", error);
      toast({
        title: "Error",
        description: "Failed to generate care plan",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (!carePlanDetails.trim()) {
      toast({
        title: "Error",
        description: "Care plan details are required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create the care plan JSON string
      const carePlanJson = carePlanDetails;

      const carePlanData: CarePlanCreate = {
        organization_id: organizationId,
        care_plan: carePlanJson
      };

      await api.uploadCarePlan(patientId, carePlanData);

      toast({
        title: "Success",
        description: carePlan ? "Care plan updated successfully" : "Care plan created successfully",
      });

      onOpenChange(false);
      resetForm();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error submitting care plan:", err);
      toast({
        title: "Error",
        description: "Failed to save care plan",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{carePlan ? "Edit Care Plan" : "Create Care Plan"}</DialogTitle>
          <DialogDescription>
            {carePlan 
              ? "Update the care plan details below." 
              : "Fill in the details to create a new care plan."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="carePlanType" className="sm:text-right">
              Care Plan Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={carePlanType}
              onValueChange={setCarePlanType}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder="Select care plan type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="chronic_condition">Chronic Condition</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!carePlan && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-start sm:gap-4">
              <Label htmlFor="instructions" className="sm:text-right sm:pt-2">
                Instructions (Optional)
              </Label>
              <Textarea
                id="instructions"
                className="sm:col-span-3 min-h-[100px]"
                placeholder="Enter any specific instructions for generating the care plan..."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                disabled={isGenerating}
              />
            </div>
          )}

          {!carePlan && !isGenerating && carePlanDetails === "" && (
            <div className="flex justify-end">
              <Button 
                onClick={handleGenerateCarePlan}
                disabled={isLoadingMedicalInfo}
                className="ml-auto"
              >
                {isLoadingMedicalInfo ? "Loading medical info..." : "Generate Care Plan"}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-start sm:gap-4">
            <Label htmlFor="carePlanDetails" className="sm:text-right sm:pt-2">
              Care Plan Details <span className="text-destructive">*</span>
            </Label>
            <Textarea
              ref={textareaRef}
              id="carePlanDetails"
              className="sm:col-span-3 min-h-[300px] font-mono text-sm"
              value={carePlanDetails}
              onChange={(e) => setCarePlanDetails(e.target.value)}
              placeholder={isGenerating ? "Generating care plan..." : "Care plan details will appear here..."}
              disabled={isGenerating}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || isGenerating}>
            {isSubmitting ? "Saving..." : carePlan ? "Update" : "Save Care Plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CarePlanModal;
